/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * The styled components for the AccountSelect
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @version 1.23.2
 * @since 1.2.0
 */

import styled from 'styled-components';

import { colors, fonts } from '../../../theme';

export const AccountSelectContainerStyled = styled.div`
    position: relative;
`;

export const AccountSelectListStyled = styled.div`
    position: absolute;
    top: 100%;
    z-index: 501;
    width: 100%;
    padding: 16px 0 8px 0;
    background-color: ${colors.accountSelectListBodyBgColor};
    box-shadow: 0 2px 12px 0 rgba(51, 51, 51, 0.2);
`;

export const AccountSelectListEmptyStyled = styled(AccountSelectListStyled)`
    box-sizing: border-box;
    padding: 16px;
    color: ${colors.accountSelectNotFoundTextColor};
`;

export const AccountSelectListHeaderStyled = styled.div`
    display: flex;
    margin-bottom: 8px;
    padding: 0 16px 0 0;
    ${fonts.textSmall};
    color: ${colors.accountSelectListHeaderTextColor};
`;

export const AccountSelectListBodyStyled = styled.div`
    max-height: 288px;
    overflow-y: auto;
    & > ul {
        padding: 0;
        margin: 0;
        list-style: none;
    }
`;

interface IAccountSelectListRowStyled {
    isPaddingRight: boolean;
}

export const AccountSelectListRowStyled = styled.li<IAccountSelectListRowStyled>`
    display: flex;
    ${fonts.textMain};
    padding: 8px 0;
    ${(props) => (props.isPaddingRight ? 'padding-right: 16px;' : '')}
    &:hover {
        cursor: pointer;
        background-color: ${colors.accountSelectListHoverBgColor};
    }
`;

export interface IAccountSelectListItem {
    width?: number;
    textAlign?: string;
}

export const AccountSelectListItemStyled = styled.div<IAccountSelectListItem>`
    ${(props) => (props.width ? `flex: 0 1 ${props.width}px` : 'flex: 1')};
    box-sizing: border-box;
    text-align: ${(props) => (props.textAlign === 'right' ? 'right' : 'left')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:first-child {
        padding-left: 16px;
    }
    &:last-child {
        padding-right: 12px;
    }
    & .account-select__list-item--text-bold {
        font-weight: bold;
        background-color: transparent;
    }
`;

export const AccountSelectButtonContainerStyled = styled.div`
    position: relative;
`;

export const AccountSelectLabelStyled = styled.label`
    display: block;
    margin-bottom: 4px;
    color: ${colors.accountSelectLabelTextColor};
    text-align: left;
    ${fonts.textMainBold};
`;

export const AccountSelectDisplayContainerStyled = styled.div`
    width: 100%;
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

interface IAccountSelectDisplayStyled {
    disabled: boolean;
    isValid?: boolean;
}

export const AccountSelectDisplayStyled = styled.div<IAccountSelectDisplayStyled>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    box-sizing: border-box;
    background-color: ${(props) =>
        props.disabled ? colors.accountSelectDisplayBgColorDisabled : colors.accountSelectDisplayBgColorDefault};
    padding: 11px 56px 11px 20px;
    border: solid 1px
        ${(props) =>
            props.disabled
                ? colors.accountSelectDisplayBorderColorDisabled
                : props.isValid
                ? colors.accountSelectDisplayBorderColorDefault
                : colors.accountSelectDisplayBorderColorError};
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`;

export interface IAccountSelectDisplayItem {
    isColorSecondary?: boolean;
    width?: number;
    textAlign?: string;
}

export const AccountSelectDisplayItemStyled = styled.div<IAccountSelectDisplayItem>`
    ${(props) => (props.width ? `flex: 0 0 ${props.width}px` : 'flex: 1')};
    text-align: ${(props) => (props.textAlign === 'right' ? 'right' : 'left')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${(props) =>
        props.isColorSecondary
            ? colors.accountSelectDisplayTextColorSecondary
            : colors.accountSelectDisplayTextColorDefault};
`;

export const AccountSelectDisplayIconStyled = styled.img`
    position: absolute;
    right: 16px;
    width: 32px;
`;

export const AccountSelectLoaderStyled = styled.div`
    position: absolute;
    bottom: 12px;
    right: 16px;
    width: 24px;
    height: 24px;
    z-index: 1;
    border-radius: 50%;
    box-sizing: border-box;
    border: 2px solid ${colors.accountSelectLoaderColor};
    border-top: 2px solid ${colors.accountSelectLoaderCircleColor};
    border-right: 2px solid ${colors.accountSelectLoaderCircleColor};
    animation: spin 2s linear infinite;
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;
